import { formatDateForAPI } from '../utils';
import { buildApiUrl } from '../config';

const API_BASE_URL = buildApiUrl('/altaEvento');

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const extractMonthFromDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString + "T12:00:00");
    return date.getMonth() + 1; // 1-12 para API
  } catch (error) {
    console.warn('Error extracting month from date:', error);
    return null;
  }
};

const mapToApiFormat = (formData) => {
  const now = new Date();
  
  return {
    pTitulo: formData.titulo?.trim() || '',
    pFecha_creacion: now.toISOString(), // Siempre usa fecha actual
    pFecha_fin: formatDateForAPI(formData.fechaFin),
    pMes: extractMonthFromDate(formData.fechaFin), // Extraer mes de fecha_fin
    pUbicacion: formData.ubicacion?.trim() || '',
    pUrl_logo: formData.imagenUrl || null,
    pRegistro: now.toISOString()
  };
};

const validateAltaEventoData = (formData) => {
  const errors = [];
  
  if (!formData.titulo || formData.titulo.trim().length === 0) {
    errors.push('El título es requerido');
  }
  
  if (formData.titulo && formData.titulo.length > 100) {
    errors.push('El título no puede exceder 100 caracteres');
  }
  
  if (!formData.ubicacion || formData.ubicacion.trim().length === 0) {
    errors.push('La ubicación es requerida');
  }
  
  if (formData.ubicacion && formData.ubicacion.length > 250) {
    errors.push('La ubicación no puede exceder 250 caracteres');
  }
  
  if (!formData.fechaFin) {
    errors.push('La fecha de fin es requerida');
  } else {
    try {
      const fechaFin = new Date(formData.fechaFin);
      const ahora = new Date();
      
      if (isNaN(fechaFin.getTime())) {
        errors.push('La fecha de fin no es válida');
      }
      
      if (fechaFin <= ahora) {
        errors.push('La fecha de fin debe ser posterior a la fecha actual');
      }
    } catch (error) {
      errors.push('La fecha de fin tiene un formato inválido');
    }
  }
  
  if (formData.imagenUrl && typeof formData.imagenUrl !== 'string') {
    errors.push('La URL de la imagen debe ser un texto válido');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const apiRequest = async (data, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  let lastError;
  const maxRetries = options.maxRetries || 2;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const backoffDelay = 1000 * Math.pow(2, attempt - 1);
        await sleep(backoffDelay);
      }
      
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        body: JSON.stringify(data),
        ...config
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      return {
        success: true,
        data: responseData,
        status: response.status,
        attempts: attempt + 1
      };
      
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        continue;
      }
      
      if (error.message.includes('404') || error.message.includes('500')) {
        break;
      }
    }
  }
  
  throw new Error(`Error en petición API después de ${maxRetries + 1} intentos: ${lastError.message}`);
};

const altaEventoService = {
  create: async (formData, imageFile = null, options = {}) => {
    try {
      // Validación de datos del formulario
      const validation = validateAltaEventoData(formData);
      if (!validation.isValid) {
        throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
      }
      
      // Subir imagen si existe
      let imagenUrl = formData.imagenUrl || null;
      if (imageFile) {
        const imageService = (await import('./imageService')).default;
        
        const uploadResult = await imageService.upload(imageFile, {
          onProgress: options.onImageProgress,
          maxRetries: 3
        });
        
        if (uploadResult.success) {
          imagenUrl = uploadResult.url;
        } else {
          throw new Error('Error al subir la imagen');
        }
      }
      
      // Preparar datos para API
      const apiData = {
        ...formData,
        imagenUrl
      };
      
      // Mapear al formato esperado por la API
      const mappedData = mapToApiFormat(apiData);
      
      // Realizar petición
      const result = await apiRequest(mappedData, {
        maxRetries: options.maxRetries || 2
      });
      
      // Verificar respuesta específica de la API
      if (result.data.error !== 0) {
        throw new Error(result.data.mensaje || 'Error al crear evento');
      }
      
      return {
        success: true,
        message: result.data.mensaje,
        eventId: result.data.eventId || null,
        attempts: result.attempts,
        imageUrl: imagenUrl
      };
      
    } catch (error) {
      console.error('Error en altaEventoService.create:', error);
      throw new Error(error.message || 'Error al crear el evento');
    }
  },
  
  validateData: validateAltaEventoData,
  
  mapToApiFormat,
  
  // Método para testing
  __test__: {
    extractMonthFromDate,
    mapToApiFormat,
    validateAltaEventoData
  }
};

export default altaEventoService;